import { Link, useLocation } from "react-router-dom";
import { footerLinks, contactInfo } from "@/mocks/homeData";
import { useState } from "react";

interface FooterMenuItem {
  label: string;
  href: string;
  children?: { label: string; href: string; children?: { label: string; href: string }[] }[];
}

const programsMenu: FooterMenuItem = {
  label: "Programs",
  href: "/programs",
  children: [
    {
      label: "Innovation Wing",
      href: "/wings/innovation",
      children: [
        { label: "PIIC", href: "/piic" },
        { label: "PTIM", href: "/ptim" },
        { label: "Training", href: "/training" },
        { label: "Experts", href: "/experts" },
      ],
    },
    {
      label: "Research Wing",
      href: "/wings/research",
      children: [
        { label: "PIARC", href: "/piarc" },
        { label: "IJTAI", href: "/ijtai" },
        { label: "AIRCon", href: "/aircon" },
      ],
    },
    {
      label: "Readiness & Recognition",
      href: "/wings/readiness",
      children: [
        { label: "Innovation Readiness Certification", href: "/wings/readiness" },
        { label: "Textile Innovation Awards", href: "/wings/readiness" },
      ],
    },
    {
      label: "Expo Wing",
      href: "/wings/expo",
      children: [
        { label: "Textile Innovation Expo", href: "https://www.textileinnovationexpo.com/" },
        { label: "Concurrent Expos", href: "/wings/expo" },
      ],
    },
  ],
};

function isExternalLink(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}

function FooterLogo() {
  return (
    <Link to="/" className="inline-flex items-center gap-3">
      <div className="rounded-lg px-3 py-2 shadow-sm">
        <img
          src="/image/logo.png"
          alt="TIE - Textile Innovation Exchange"
          className="h-20 md:h-20 w-auto object-contain"
        />
      </div>
    </Link>
  );
}

function FooterQuickLink({ href, label }: { href: string; label: string }) {
  const location = useLocation();
  const isHome = location.pathname === "/";

  if (isExternalLink(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-white/70 hover:text-tie-teal-light transition-colors inline-flex items-center gap-1"
      >
        {label}
        <i className="ri-external-link-line text-xs" />
      </a>
    );
  }

  const handleClick = (e: React.MouseEvent) => {
    if (href.startsWith("#")) {
      if (isHome) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const to = href.startsWith("#") && !isHome ? `/${href}` : href;

  return (
    <Link
      to={to}
      onClick={handleClick}
      className="text-sm text-white/70 hover:text-tie-teal-light transition-colors"
    >
      {label}
    </Link>
  );
}

function FooterResourceLink({ href, label }: { href: string; label: string }) {
  if (isExternalLink(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-white/70 hover:text-tie-teal-light transition-colors inline-flex items-center gap-1"
      >
        {label}
        <i className="ri-external-link-line text-xs" />
      </a>
    );
  }
  return (
    <Link
      to={href}
      className="text-sm text-white/70 hover:text-tie-teal-light transition-colors"
    >
      {label}
    </Link>
  );
}

function FooterProgramsBox() {
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 md:p-8 mb-10 md:mb-14">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between lg:cursor-default"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50">
          Programmes
        </h4>
        <i className={`ri-arrow-down-s-line lg:hidden text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[800px] opacity-100 mt-5" : "max-h-0 opacity-0 mt-0 lg:max-h-[800px] lg:opacity-100 lg:mt-5"}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {programsMenu.children?.map((wing) => (
            <div key={wing.label}>
              <Link
                to={wing.href}
                className="block text-sm font-semibold text-white/90 hover:text-tie-teal-light transition-colors mb-3"
              >
                {wing.label}
              </Link>
              <ul className="space-y-2">
                {wing.children?.map((prog) => {
                  const external = isExternalLink(prog.href);
                  return external ? (
                    <li key={prog.label}>
                      <a
                        href={prog.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-white/60 hover:text-tie-teal-light transition-colors inline-flex items-center gap-1"
                      >
                        {prog.label}
                        <i className="ri-external-link-line text-xs" />
                      </a>
                    </li>
                  ) : (
                    <li key={prog.label}>
                      <Link
                        to={prog.href}
                        className="text-sm text-white/60 hover:text-tie-teal-light transition-colors"
                      >
                        {prog.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-tie-navy text-white">
      {/* Upper Section */}
      <div className="w-full px-4 md:px-8 lg:px-12 pt-12 md:pt-16">
        {/* Programs — Full Width Box */}
        <FooterProgramsBox />

        {/* Rest of Footer Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 pb-12 md:pb-16">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <FooterLogo />
            <p className="mt-4 text-sm text-white/60 leading-relaxed">
              Textile Innovation Exchange - Turning innovativeness into the core
              competence of Bangladesh&apos;s textile and apparel industry.
            </p>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">
              {contactInfo.address}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.href}>
                  <FooterQuickLink href={link.href} label={link.label} />
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
              Resources
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.resources.map((link) => (
                <li key={link.href}>
                  <FooterResourceLink href={link.href} label={link.label} />
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
              Connect
            </h4>
            <div className="space-y-2 mb-5">
              <p className="text-sm text-white/70">{contactInfo.phone}</p>
              <p className="text-sm text-white/70">{contactInfo.email}</p>
              <p className="text-sm text-white/50">Partnership: {contactInfo.partnershipEmail}</p>
            </div>
            <div className="flex items-center gap-3">
              {footerLinks.social.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-tie-teal hover:text-white transition-all"
                  aria-label={social.label}
                >
                  <i className={social.icon} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lower Bar */}
      <div className="border-t border-white/10">
        <div className="w-full px-4 md:px-8 lg:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/50">
            &copy; {new Date().getFullYear()} Textile Innovation Exchange. All
            rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>In Association with BKMEA &amp; BGBA</span>
          </div>
        </div>
      </div>

      {/* Giant TIE Letters */}
      <div className="overflow-hidden pb-4 md:pb-6">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <span className="font-serif text-[80px] md:text-[120px] lg:text-[160px] font-bold text-tie-teal/10 leading-none tracking-tight select-none block">
            TIE
          </span>
        </div>
      </div>
    </footer>
  );
}